import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

interface PopoverProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  contentClassName?: string;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  align?: 'start' | 'center' | 'end';
  spacing?: 'minimal' | 'tight' | 'normal';
  /**
   * How the panel is positioned.
   *
   * `absolute` (default) — the panel is an absolutely-positioned child of the
   * trigger wrapper. Simple, and correct for a trigger that isn't inside a
   * scroll container (toolbar filter popovers, table row menus).
   *
   * `fixed` — the panel is portaled to <body> and pinned with viewport
   * coordinates, so no ancestor's `overflow` can clip it. It also flips above
   * the trigger when there's no room below, clamps to the viewport, and
   * re-measures on scroll. Opt into this when the trigger lives inside a
   * scrolling/clipping container — e.g. a message in the helpdesk ticket
   * thread, where the `absolute` panel is cut off at the scroller's edge.
   *
   * Note: `contentClassName` positioning utilities (`bottom-full`, `mt-*`) only
   * apply under `absolute` — `fixed` sets top/left inline.
   */
  strategy?: 'absolute' | 'fixed';
}

/** Gap in px between the trigger and the panel, per `spacing` (fixed strategy). */
const SPACING_PX = { minimal: 2, tight: 4, normal: 8 } as const;
/** Minimum breathing room between the panel and the viewport edge. */
const VIEWPORT_MARGIN = 8;

export function Popover({
  trigger,
  children,
  contentClassName,
  isOpen: controlledIsOpen,
  onOpenChange,
  align = 'center',
  spacing = 'normal',
  strategy = 'absolute',
}: PopoverProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const isOpen = controlledIsOpen ?? internalIsOpen;
  const setIsOpen = onOpenChange ?? setInternalIsOpen;
  const isFixed = strategy === 'fixed';

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Don't close popover if clicking on ComboBox dropdown elements
      if (target.closest('[data-combobox-dropdown="true"]')) {
        return;
      }

      // Under the `fixed` strategy the panel is portaled to <body>, so it is
      // NOT a DOM descendant of the trigger wrapper — both refs are checked.
      // Under `absolute` the panel is inside the wrapper and the trigger check
      // alone already covers it.
      if (contentRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  // `fixed` only: measure the trigger and panel, then pin the panel with
  // viewport coordinates, flipping above the trigger when there's no room below.
  useLayoutEffect(() => {
    if (!isFixed || !isOpen) {
      setPosition(null);
      return;
    }

    const compute = () => {
      const triggerEl = triggerRef.current;
      const contentEl = contentRef.current;
      if (!triggerEl || !contentEl) return;

      const t = triggerEl.getBoundingClientRect();
      const c = contentEl.getBoundingClientRect();
      const gap = SPACING_PX[spacing];
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Below the trigger by default; flip above only when it doesn't fit
      // below AND it does fit above.
      let top = t.bottom + gap;
      if (
        top + c.height > viewportHeight - VIEWPORT_MARGIN &&
        t.top - gap - c.height >= VIEWPORT_MARGIN
      ) {
        top = t.top - gap - c.height;
      }
      // Panel taller than the space either side of the trigger: pin it inside
      // the viewport rather than letting it run off the edge.
      top = Math.max(VIEWPORT_MARGIN, Math.min(top, viewportHeight - VIEWPORT_MARGIN - c.height));

      let left =
        align === 'start'
          ? t.left
          : align === 'end'
            ? t.right - c.width
            : t.left + t.width / 2 - c.width / 2;
      left = Math.max(VIEWPORT_MARGIN, Math.min(left, viewportWidth - VIEWPORT_MARGIN - c.width));

      setPosition({ top, left });
    };

    compute();
    // `fixed` coordinates go stale the moment anything scrolls, so re-measure.
    // Capture phase catches scrolls in nested containers, not just the window.
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [isFixed, isOpen, align, spacing]);

  const alignmentClasses = {
    start: 'left-0',
    center: 'left-1/2 -translate-x-1/2',
    end: 'right-0',
  };

  const spacingClasses = {
    minimal: 'mt-0.5',
    tight: 'mt-1',
    normal: 'mt-2',
  };

  const panelClasses =
    'rounded-md border bg-white dark:bg-gray-800 text-foreground shadow-md outline-none animate-in data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 border-gray-200 dark:border-gray-700';

  const panel = isFixed ? (
    <div
      ref={contentRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        // Hidden for the first paint only — the pre-measurement pass at (0,0)
        // must not be visible.
        opacity: position ? 1 : 0,
      }}
      className={cn('fixed z-[100000]', panelClasses, contentClassName)}
    >
      {children}
    </div>
  ) : (
    <div
      ref={contentRef}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'absolute z-[100000]',
        panelClasses,
        spacingClasses[spacing],
        alignmentClasses[align],
        contentClassName
      )}
    >
      {children}
    </div>
  );

  return (
    <div className="relative" ref={triggerRef}>
      <div onClick={handleToggle} className="cursor-pointer">
        {trigger}
      </div>
      {isOpen && (isFixed ? createPortal(panel, document.body) : panel)}
    </div>
  );
}
